import { Building, ChevronUp, ChevronDown, Settings, HelpCircle, LogOut } from "lucide-react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { cn } from "@/utils";
import { useOrganizations } from "@/hooks/organization";
import { useDropdown } from "@/hooks/useDropdown";
import { useHubProfileSafe } from "@/hooks/hub/useHubProfileSafe";
import { ProfileSwitcher } from "@/components/shared";
import type { SidebarVariant } from "../Sidebar/sidebarTheme";

interface Organization {
  uuid: string;
  name: string;
  logo_signed_url?: string | null;
}

interface UserOrganization {
  organization: Organization;
  role: string;
}

interface UserMenuDropdownProps {
  isCollapsed: boolean;
  organizationDetails?: Organization | null;
  currentOrganization?: { user_role?: string } | null;
  userOrganizations?: UserOrganization[] | null;
  onClose: () => void;
  onLogout: () => void;
  variant?: SidebarVariant;
}

export const UserMenuDropdown = ({
  isCollapsed,
  organizationDetails,
  currentOrganization,
  userOrganizations,
  onClose,
  onLogout,
  variant = "default",
}: UserMenuDropdownProps) => {
  const { t } = useTranslation();
  const { switchOrganization } = useOrganizations();
  const orgDropdown = useDropdown();

  // Hub profile — safe hook that returns null on non-hub routes
  const hubProfileContext = useHubProfileSafe();
  const hubProfile = variant === "hub" ? hubProfileContext : null;

  // Calculate other organizations
  const currentOrgUuid = currentOrganization?.user_role ?? organizationDetails?.uuid ?? null;

  const otherOrgs = (userOrganizations ?? []).filter((uo) => {
    const ouuid = uo?.organization?.uuid ?? null;
    const sameByUuid = currentOrgUuid != null && ouuid === currentOrgUuid;
    return !sameByUuid;
  });

  const hasMultipleOrgs = (userOrganizations?.length ?? 0) > 1;

  return (
    <div
      className={cn(
        "absolute bottom-full mb-2 left-0 bg-bg-primary rounded-md shadow-lg border border-border-secondary py-1 z-popover",
        orgDropdown.isOpen ? "overflow-visible" : "overflow-hidden",
        isCollapsed ? "w-60" : "w-full",
      )}
    >
      {/* Hub Profile Switcher */}
      {variant === "hub" && hubProfile?.selectedProfile && (
        <div className="px-4 pt-2 pb-3">
          <ProfileSwitcher
            selectedProfile={hubProfile.selectedProfile}
            profiles={hubProfile.profiles}
            onProfileSelect={hubProfile.selectProfile}
            variant="dropdown"
            onClose={onClose}
          />
        </div>
      )}

      {/* Organization Switcher */}
      {organizationDetails && variant === "default" && (
        <div className="px-4 pt-2 pb-3">
          <div ref={orgDropdown.ref} className="relative">
            {/* Current organization button */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                if (hasMultipleOrgs) orgDropdown.toggle();
              }}
              disabled={!hasMultipleOrgs}
              className={cn(
                "w-full flex items-center justify-between rounded-md transition-all duration-200 shadow-sm border border-border-secondary p-3",
                hasMultipleOrgs
                  ? orgDropdown.isOpen
                    ? "bg-hover-light rounded-t-none border-t-0 shadow-lg"
                    : "bg-bg-primary hover:bg-hover-light hover:shadow-md hover:scale-102 cursor-pointer"
                  : "bg-bg-primary cursor-default",
              )}
            >
              <div className="flex items-center space-x-2 flex-1 min-w-0">
                <div className="w-6 h-6 rounded-md overflow-visible flex-shrink-0 relative">
                  {organizationDetails.logo_signed_url ? (
                    <img
                      src={organizationDetails.logo_signed_url}
                      alt={organizationDetails.name}
                      className="w-full h-full object-cover rounded-md"
                    />
                  ) : (
                    <div className="w-full h-full bg-bg-secondary flex items-center justify-center rounded-md">
                      <Building size={12} className="text-text-secondary" />
                    </div>
                  )}
                  {/* Green dot indicator for current organization */}
                  <div className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-success-bar-light rounded-full border border-bg-primary z-10"></div>
                </div>
                <div className="flex-1 min-w-0 text-left">
                  <p className="text-xs font-medium text-text-primary truncate">
                    {organizationDetails.name}
                  </p>
                </div>
              </div>
              {hasMultipleOrgs && (
                <div className="flex flex-col items-center ml-1 flex-shrink-0">
                  <ChevronUp
                    size={10}
                    className={cn(
                      "text-text-secondary transition-opacity",
                      orgDropdown.isOpen ? "opacity-100" : "opacity-50",
                    )}
                  />
                  <ChevronDown
                    size={10}
                    className={cn(
                      "text-text-secondary transition-opacity -mt-0.5",
                      orgDropdown.isOpen ? "opacity-50" : "opacity-100",
                    )}
                  />
                </div>
              )}
            </button>

            {/* Other organizations - appear above current org */}
            {hasMultipleOrgs && orgDropdown.isOpen && otherOrgs.length > 0 && (
              <div
                className="absolute bottom-full left-0 right-0 z-modal-backdrop"
                role="presentation"
                onKeyDown={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
              >
                <div className="bg-bg-primary border border-border-secondary border-b-0 rounded-t-md shadow-lg max-h-60 overflow-auto">
                  {otherOrgs.map((userOrg, index) => (
                    <button
                      key={userOrg.organization.uuid}
                      className={cn(
                        "w-full flex items-center justify-between p-3 hover:bg-hover-light cursor-pointer transition-colors",
                        index === 0 && "rounded-t-md",
                      )}
                      onClick={async (e) => {
                        e.stopPropagation();
                        await switchOrganization(userOrg.organization.uuid);
                        orgDropdown.close();
                      }}
                    >
                      <div className="flex items-center space-x-2 flex-1 min-w-0">
                        <div className="w-6 h-6 rounded-md overflow-hidden flex-shrink-0">
                          {userOrg.organization.logo_signed_url ? (
                            <img
                              src={userOrg.organization.logo_signed_url}
                              alt={userOrg.organization.name}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full bg-bg-secondary flex items-center justify-center">
                              <Building size={12} className="text-text-secondary" />
                            </div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0 text-left">
                          <p className="text-xs font-medium text-text-primary truncate">
                            {userOrg.organization.name}
                          </p>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Menu Items */}
      {variant === "admin" && (
        <Link
          to="/"
          className="flex items-center px-4 py-2 text-sm text-text-primary hover:bg-hover-light hover:shadow-sm transition-colors duration-200"
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
        >
          <Building size={16} className="mr-2" />
          {t("admin.nav.back_to_app") || "Volver a la App"}
        </Link>
      )}

      {variant !== "hub" && (
        <>
          <Link
            to="/settings"
            className="flex items-center px-4 py-2 text-sm text-text-primary hover:bg-hover-light hover:shadow-sm transition-colors duration-200"
            onClick={(e) => {
              e.stopPropagation();
              onClose();
            }}
          >
            <Settings size={16} className="mr-2" />
            {t("nav.settings")}
          </Link>
          <Link
            to="/help"
            className="flex items-center px-4 py-2 text-sm text-text-primary hover:bg-hover-light hover:shadow-sm transition-colors duration-200"
            onClick={(e) => {
              e.stopPropagation();
              onClose();
            }}
          >
            <HelpCircle size={16} className="mr-2" />
            {t("nav.help")}
          </Link>
          <div className="border-t border-border-secondary my-1"></div>
        </>
      )}
      <button
        className="w-full flex items-center px-4 py-2 text-sm text-danger hover:bg-hover-light hover:shadow-sm transition-colors duration-200"
        onClick={(e) => {
          e.stopPropagation();
          onClose();
          onLogout();
        }}
      >
        <LogOut size={16} className="mr-2" />
        {t("nav.logout")}
      </button>
    </div>
  );
};
